import type { RecentLocation } from '../components/LocationPicker/types';

interface LocationSearchResult {
  id: string;
  name: string;
  description: string;
  city?: string; // City name (extracted from reverse geocoding or IP geolocation)
  latitude: number;
  longitude: number;
}

interface LastSearchLocation {
  latitude: number;
  longitude: number;
  name: string;
}

class LocationService {
  private static RECENT_LOCATIONS_KEY = 'toptoptime_recent_locations';
  private static LAST_SEARCH_LOCATION_KEY = 'toptoptime_last_search_location';
  private static MAX_RECENT_LOCATIONS = 5;

  /**
   * Search for locations using backend API (avoids CORS issues)
   */
  async searchLocations(query: string): Promise<LocationSearchResult[]> {
    if (!query || query.trim().length < 2) {
      return [];
    }

    try {
      // Use our backend API to avoid CORS issues
      const response = await fetch(`/api/location/search?q=${encodeURIComponent(query)}`);

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      return data.results || [];
    } catch {
      return [];
    }
  }

  /**
   * Get user location based on IP address (fallback method)
   */
  async getLocationByIP(): Promise<LocationSearchResult | null> {
    try {
      const response = await fetch('/api/location/ip');

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        // Handle reserved IP address (common in development)
        if (errorData.isReservedIP || errorData.error === 'Reserved IP Address') {
          // Silent fail for reserved IP (normal in development)
          return null;
        }

        return null;
      }

      const locationData = await response.json();

      return {
        id: 'ip-location',
        name: locationData.name,
        description: locationData.description,
        city: locationData.name, // IP geolocation returns city name directly in the name field
        latitude: locationData.latitude,
        longitude: locationData.longitude
      };
    } catch {
      return null;
    }
  }

  /**
   * Get current user location using browser Geolocation API
   * Falls back to IP-based geolocation if browser geolocation fails
   */
  async getCurrentLocation(): Promise<LocationSearchResult | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        // Try IP fallback when geolocation is not supported
        this.getLocationByIP()
          .then(resolve)
          .catch(() => resolve(null));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            // Round coordinates to 2 decimal places (~1km accuracy) for city-level matching
            // This provides good balance between accuracy and cache efficiency
            const roundedLat = Number(position.coords.latitude.toFixed(2));
            const roundedLon = Number(position.coords.longitude.toFixed(2));

            // Use backend API for reverse geocoding
            const response = await fetch(`/api/location/reverse?lat=${roundedLat}&lon=${roundedLon}`);

            if (!response.ok) {
              throw new Error('Reverse geocoding failed');
            }

            const locationData = await response.json();

            // Check if we got a valid city name
            if (locationData.city || (locationData.name && locationData.name !== 'Unknown Location')) {
              resolve({
                id: 'current-location',
                name: locationData.name,
                description: locationData.description,
                city: locationData.city || undefined, // Use city field if available
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              });
            } else {
              // Reverse geocoding succeeded but no city found, try IP fallback
              try {
                const ipLocation = await this.getLocationByIP();
                if (ipLocation && ipLocation.city) {
                  resolve(ipLocation);
                  return;
                }
              } catch {
                // IP fallback failed, continue with reverse geocoding result
              }
              // Use reverse geocoding result even without city
              resolve({
                id: 'current-location',
                name: locationData.name || 'Current Location',
                description:
                  locationData.description ||
                  `${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)}`,
                city: locationData.city || undefined,
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              });
            }
          } catch {
            // Try IP fallback when reverse geocoding fails
            try {
              const ipLocation = await this.getLocationByIP();
              if (ipLocation) {
                resolve(ipLocation);
                return;
              }
            } catch {
              // IP fallback failed, continue to coordinates fallback
            }
            // Final fallback to just coordinates if everything fails
            resolve({
              id: 'current-location',
              name: 'Current Location',
              description: `${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)}`,
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
          }
        },
        async () => {
          // Try IP fallback when browser geolocation fails
          try {
            const ipLocation = await this.getLocationByIP();
            resolve(ipLocation);
          } catch {
            resolve(null);
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  }

  /**
   * Reverse geocode coordinates into a human-readable location name
   */
  async reverseGeocode(lat: number, lon: number): Promise<LocationSearchResult | null> {
    try {
      const roundedLat = Number(lat.toFixed(2));
      const roundedLon = Number(lon.toFixed(2));
      const response = await fetch(`/api/location/reverse?lat=${roundedLat}&lon=${roundedLon}`);
      if (!response.ok) return null;

      const data = await response.json();
      if (!data?.name || data.name === 'Unknown Location') return null;

      return {
        id: `reverse-${roundedLat}-${roundedLon}`,
        name: data.name,
        description: data.description,
        city: data.city || undefined,
        latitude: lat,
        longitude: lon
      };
    } catch {
      return null;
    }
  }

  /**
   * Save a location to recent locations
   */
  saveRecentLocation(location: RecentLocation): void {
    try {
      const recentLocations = this.getRecentLocations();

      // Remove if already exists
      const filtered = recentLocations.filter((loc) => loc.id !== location.id);

      // Add to beginning
      filtered.unshift(location);

      // Keep only max number of locations
      const toSave = filtered.slice(0, LocationService.MAX_RECENT_LOCATIONS);

      localStorage.setItem(LocationService.RECENT_LOCATIONS_KEY, JSON.stringify(toSave));
    } catch (error) {
      console.error('Error saving recent location:', error);
    }
  }

  /**
   * Get recent locations from localStorage
   */
  getRecentLocations(): RecentLocation[] {
    try {
      const stored = localStorage.getItem(LocationService.RECENT_LOCATIONS_KEY);
      if (!stored) return [];

      const locations = JSON.parse(stored);
      return Array.isArray(locations) ? locations : [];
    } catch (error) {
      console.error('Error getting recent locations:', error);
      return [];
    }
  }

  /**
   * Save the last searched location to localStorage
   */
  saveLastSearchLocation(location: LastSearchLocation): void {
    try {
      localStorage.setItem(LocationService.LAST_SEARCH_LOCATION_KEY, JSON.stringify(location));
    } catch (error) {
      console.error('Error saving last search location:', error);
    }
  }

  /**
   * Get the last searched location from localStorage
   */
  getLastSearchLocation(): LastSearchLocation | null {
    try {
      const stored = localStorage.getItem(LocationService.LAST_SEARCH_LOCATION_KEY);
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      if (
        typeof parsed?.latitude === 'number' &&
        typeof parsed?.longitude === 'number' &&
        typeof parsed?.name === 'string'
      ) {
        return parsed as LastSearchLocation;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Clear all recent locations
   */
  clearRecentLocations(): void {
    try {
      localStorage.removeItem(LocationService.RECENT_LOCATIONS_KEY);
    } catch (error) {
      console.error('Error clearing recent locations:', error);
    }
  }

  /**
   * DEPRECATED: No longer needed - coordinates are returned with search results
   */
  async getPlaceDetails(_placeId: string): Promise<LocationSearchResult | null> {
    console.log('[LocationService] getPlaceDetails called but not needed - coordinates already in search results');
    return null;
  }
}

// Export singleton instance
export const locationService = new LocationService();
